const API_URL = process.env.REACT_APP_API_URL;
const SERVICE_URL = `${API_URL}/api/services`;

export const fetchServices = async (type = 'all', page = 1, limit = 24) => {
    try {
        const response = await fetch(`${SERVICE_URL}?page=${page}&limit=${limit}&type=${type}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch services');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching services:', error);
        throw error;
    }
};

export const addService = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to add service');
        }

        return await response.json();
    } catch (error) {
        console.error('Error adding service:', error);
        throw error;
    }
};

export const removeService = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/remove`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to remove service');
        }

        return await response.json();
    } catch (error) {
        console.error('Error removing service:', error);
        throw error;
    }
};

export const toggleService = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/toggle`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to toggle service');
        }

        return await response.json();
    } catch (error) {
        console.error('Error toggling service:', error);
        throw error;
    }
};

/*
 * Tools API
 */
export const toggleTool = async (serviceId, toolId) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/tools/${toolId}/toggle`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to toggle tool');
        }

        return await response.json();
    } catch (error) {
        console.error('Error toggling tool:', error);
        throw error;
    }
};

export const fetchServiceTools = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/tools`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to get tools');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting tools:', error);
        throw error;
    }
};

/*
 * Secrets API
 */
export const getServiceSecrets = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/secrets`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to get service secrets');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting service secrets:', error);
        throw error;
    }
};

export const getUserSecrets = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/user-secrets`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to get user secrets');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting user secrets:', error);
        throw error;
    }
};

export const upsertServiceSecret = async (serviceId, key, value, isEncrypted = true) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/secrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ key, value, isEncrypted }),
        });

        if (!response.ok) {
            throw new Error('Failed to create/update service secret');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating/updating service secret:', error);
        throw error;
    }
};

export const upsertUserSecret = async (serviceId, key, value, isEncrypted = true) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/user-secrets`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ key, value, isEncrypted }),
        });

        if (!response.ok) {
            throw new Error('Failed to create/update user secret');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating/updating user secret:', error);
        throw error;
    }
};

export const deleteServiceSecret = async (serviceId, key) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/secrets/${key}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to delete service secret');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting service secret:', error);
        throw error;
    }
};

export const deleteUserSecret = async (serviceId, key) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/user-secrets/${key}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to delete user secret');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting user secret:', error);
        throw error;
    }
};
/*
 * End of Secrets API
 */

/*
 * OAuth API
 */
export const getServiceOAuth = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/oauth`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to get OAuth settings');
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting OAuth settings:', error);
        throw error;
    }
};

export const upsertServiceOAuth = async (serviceId, oauthData) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/oauth`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(oauthData),
        });

        if (!response.ok) {
            throw new Error('Failed to create/update OAuth settings');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating/updating OAuth settings:', error);
        throw error;
    }
};

export const deleteServiceOAuth = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/oauth`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to delete OAuth settings');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting OAuth settings:', error);
        throw error;
    }
};

export const startOAuthFlow = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/oauth/authorize`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to start OAuth flow');
        }

        return await response.json();
    } catch (error) {
        console.error('Error starting OAuth flow:', error);
        throw error;
    }
};

export const revokeOAuthToken = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/oauth/revoke`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to revoke OAuth token');
        }

        return await response.json();
    } catch (error) {
        console.error('Error revoking OAuth token:', error);
        throw error;
    }
};

export const processOAuthCallback = async queryString => {
    try {
        const response = await fetch(`${SERVICE_URL}/oauth/callback${queryString}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to process OAuth callback');
        }

        return { success: true };
    } catch (error) {
        console.error('Error processing OAuth callback:', error);
        throw error;
    }
};
/*
 * End of OAuth API
 */

/*
 * Service & Tool Details API
 */
export const fetchServiceDetails = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/details`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            throw new Error('Failed to fetch service details');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching service details:', error);
        throw error;
    }
};

export const updateService = async (serviceId, serviceData) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(serviceData),
        });

        if (!response.ok) {
            throw new Error('Failed to update service');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating service:', error);
        throw error;
    }
};

export const uploadServiceLogo = async (serviceId, logoFile) => {
    try {
        const formData = new FormData();
        formData.append('logo', logoFile);

        const response = await fetch(`${SERVICE_URL}/${serviceId}/logo`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'Failed to upload service logo');
        }

        return data;
    } catch (error) {
        console.error('Error uploading service logo:', error);
        throw error;
    }
};

export const deleteServiceLogo = async serviceId => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/logo`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete service logo');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting service logo:', error);
        throw error;
    }
};

export const updateServiceTool = async (serviceId, toolId, toolData) => {
    try {
        const response = await fetch(`${SERVICE_URL}/${serviceId}/tools/${toolId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(toolData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update service tool');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating service tool:', error);
        throw error;
    }
};
/*
 * End of Service & Tool Details API
 */

/*
 * Service Creation API
 */
export const checkSlugAvailability = async slug => {
    try {
        const response = await fetch(`${SERVICE_URL}/check-slug/${slug}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error checking slug availability:', error);
        throw error;
    }
};

export const createService = async serviceData => {
    try {
        const response = await fetch(`${SERVICE_URL}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(serviceData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create service');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating service:', error);
        throw error;
    }
};
/*
 * End of Service Creation API
 */
